"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight, ChevronDown, X } from "lucide-react";
import { MediaPlayerInstance } from "@vidstack/react";
import { cn } from "@/lib/utils";
import { useDebouncedCallback } from "@react-hookz/web";
import { SanityVideoPlayer } from "@/components/ui/SanityVideoPlayer";

import cover from "@/public/launch/v3-cover.png";

const SESSION_KEY = "v3LaunchModalClosed";

/**
 * Mux playback id of `Trademania_Plataforma motion_V6_1920x1080.mp4`
 * (public playback policy).
 */
const PLAYBACK_ID = "71upHiiR7TQzVYnpp3kzS13j8rsMCZR00kbGQ01bB3u2I";

/** The source file is 116.8 s; replaced by the player's own reading once known. */
const FALLBACK_DURATION_SECONDS = 116.8;

function formatDuration(seconds: number) {
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes}:${rest.toString().padStart(2, "0")}`;
}

export default function V3LaunchDialog({
  language,
  className,
}: {
  language?: "en" | "de";
  className?: string;
}) {
  const pathname = usePathname();

  const playerRef = useRef<MediaPlayerInstance>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState(FALLBACK_DURATION_SECONDS);
  const [hasPlayed, setHasPlayed] = useState(false);
  // A ref, not state: the leave handler must read it without a re-render
  const isPlayingRef = useRef(false);

  const debouncedSetIsExpanded = useDebouncedCallback(
    (value) => {
      setIsExpanded(value);
    },
    [],
    100,
  );

  // Leaving the card collapses it — unless the video is playing, in which
  // case the card stays open until the user pauses it or collapses it by hand
  const handleMouseLeave = () => {
    if (isPlayingRef.current) return;
    debouncedSetIsExpanded(false);
  };

  // If user already closed the modal, don't show it again in the same session
  useEffect(() => {
    if (!sessionStorage.getItem(SESSION_KEY)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOpen(true);
    }
  }, []);

  // Collapsing the card pauses the video; it must not keep playing unseen
  useEffect(() => {
    if (!playerRef?.current) return;

    if (!isExpanded && !playerRef.current?.paused) {
      playerRef.current.pause();
    }
  }, [isExpanded, playerRef, open]);

  if (!open || pathname.includes("/admin")) return null;

  const titleClassName =
    "text-white/70 text-[1.75rem] flex flex-col justify-center min-w-0";
  const titleContent = (
    <>
      <span className="text-white text-[2rem] truncate">
        {language === "en"
          ? "Get to know Trademania 3.0"
          : "Trademania 3.0 kennenlernen"}
      </span>
      <span className="text-white/70 text-[1.75rem] truncate flex items-center gap-[.5rem] hover:text-white transition-colors duration-300 hover:underline [&_img]:hover:translate-x-[.25rem]">
        {language === "en" ? "Here's the video with all the details" : "Hier das Video mit allen Details"}
        <ArrowRight className="w-[1.75rem] h-[1.75rem] min-w-[1.75rem] transition-transform duration-300" />
      </span>
    </>
  );

  return (
    <div
      className={cn("w-fit flex gap-2 relative", className)}
      onMouseEnter={() => debouncedSetIsExpanded(true)}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={cn(
          "group font-geist transition-all duration-300 max-w-[77vw] rounded-[1.5rem] backdrop-blur-[12.5rem] bg-[#0A1A1D] border border-white/20 p-[.75rem] overflow-clip",
          isExpanded
            ? "h-[34.5rem] lg:h-[42rem] w-[60rem]"
            : "w-[42rem] lg:w-[49rem] h-[7.5rem] cursor-pointer",
        )}
        onClick={() => {
          if (!isExpanded) setIsExpanded(true);
        }}
      >
        {/* header */}
        <div className="flex gap-[1.5rem] justify-between mb-[1rem]">
          <div className="flex gap-[1.5rem] min-w-0">
            {/* cover thumbnail */}
            <div className="w-[9rem] h-[6rem] min-w-[9rem] min-h-[6rem] rounded-[1rem] border border-white/[.12] relative overflow-clip">
              <Image
                src={cover}
                alt={"Thumbnail"}
                className="w-full h-full object-cover"
                sizes="72px"
              />
            </div>
            {/* end cover thumbnail */}

            {/* Collapsed, the whole card is one expand target, so the copy
                is plain text; expanded, it becomes the link to the platform */}
            {isExpanded ? (
              <a
                href="https://trademania.app/"
                target="_blank"
                rel="noopener noreferrer"
                className={titleClassName}
              >
                {titleContent}
              </a>
            ) : (
              <div className={titleClassName}>{titleContent}</div>
            )}
          </div>
          <button
            type="button"
            className={cn(
              "hidden lg:block mt-3 me-1 self-start text-white transition-all duration-300",
              isExpanded && "me-2",
            )}
            aria-label={isExpanded ? "Collapse" : "Expand"}
            onClick={(event) => {
              event.stopPropagation();
              setIsExpanded((lastValue) => !lastValue);
            }}
          >
            <ChevronDown
              className={cn(
                "transition-transform duration-300 w-[3rem] h-[3rem] min-w-[3rem] min-h-[3rem]",
                isExpanded && "rotate-180",
              )}
            />
          </button>
        </div>
        {/* end header */}

        {/* video */}
        <div className="relative w-full aspect-video rounded-[6px] border border-white/10 bg-black overflow-clip">
          <SanityVideoPlayer
            ref={playerRef}
            className="w-full h-full"
            playerData={{
              sourceType: "mux",
              embed: false,
              embedUrl: "",
              thumbnail: "your-thumbnail-url",
              muxVideo: {
                playbackId: PLAYBACK_ID,
                assetId: "your-asset-id",
                filename: "your-filename",
                status: "your-status",
              },
            }}
            playsinline={true}
            onDurationChange={(seconds) => {
              if (Number.isFinite(seconds) && seconds > 0) setDuration(seconds);
            }}
            onPlay={() => {
              isPlayingRef.current = true;
              setHasPlayed(true);
            }}
            onPause={() => {
              isPlayingRef.current = false;
            }}
            onEnded={() => {
              isPlayingRef.current = false;
            }}
          />
          {/* Until the first play the layout answers to its centred button
              alone, so the whole poster is a click target that starts the
              video; afterwards the layout's own gestures take over */}
          {!hasPlayed && (
            <button
              type="button"
              className="absolute inset-0 z-10 cursor-pointer"
              aria-label={"Play"}
              onClick={() => void playerRef.current?.play()}
            />
          )}
          {/* duration badge: on the poster, gone once the video has played */}
          {!hasPlayed && (
            <span className="absolute z-20 right-[15px] bottom-[16px] rounded-[2px] bg-black/[.64] px-[6px] py-[3px] text-[14px] leading-none text-white pointer-events-none">
              {formatDuration(duration)}
            </span>
          )}
          {/* end duration badge */}
        </div>
        {/* end video */}
      </div>
      {/* close button */}
      {/* <button
        className="h-full text-black/80 hover:text-black transition-colors duration-300 py-4"
        onClick={() => {
          setOpen(false);
          sessionStorage.setItem(SESSION_KEY, "true");
        }}
        aria-label={"Close"}
      >
        <X className="mix-blend-difference" />
      </button> */}
      {/* end close button */}
    </div>
  );
}
